/** Route prefixes accessible without Auth0 login or cookie impersonation. */
export const NON_ADMIN_PUBLIC_ROUTE_PREFIXES = [
  '/impersonate',
  // OAuth redirect_uri after loginWithRedirect — must not re-trigger login while Auth0 hydrates
  '/forward',
  '/login',
  '/cross-device-login',
  '/signup',
  '/forgotPassword',
  '/resetPassword',
  '/confirmEmail',
  '/password-change-sent',
  '/referral',
] as const;

export function isNonAdminPublicRoute(pathname: string): boolean {
  return NON_ADMIN_PUBLIC_ROUTE_PREFIXES.some((route) => pathname.startsWith(route));
}

export function parseForwardUrlFromSearch(search: string): string | undefined {
  let forwardUrl = search.split('forwardUrl=')[1];

  if (forwardUrl && !forwardUrl.match(/login$/)) {
    forwardUrl = decodeURIComponent(forwardUrl);
  }

  if (forwardUrl && !forwardUrl.match(/login$/)) {
    return forwardUrl;
  }

  return undefined;
}

export function parseShareIdFromSearch(search: string): string | undefined {
  let shareId = search.split('shareId=')[1];

  if (shareId) {
    shareId = decodeURIComponent(shareId);
    return shareId;
  }

  return undefined;
}

export function buildAuthRedirectUri(
  pathname: string,
  search: string,
  hash: string,
  origin: string,
): string {
  let forwardUrlEncoded = pathname;
  if (search) {
    forwardUrlEncoded += search;
  }
  if (hash) {
    forwardUrlEncoded += hash;
  }

  return `${origin}/forward?forwardUrl=${encodeURIComponent(forwardUrlEncoded)}`;
}

export type FundOpportunityLookupResult<T> = {
  investor: T | null;
  error: string | null;
  stillLoading: boolean;
};

export function resolveInvestorForFundOpportunity<T extends { fundOpportunityId?: string | null }>(
  investors: T[] | null,
  fundOpportunityId: string | undefined,
  isLoading: boolean,
  options?: { requireAdvisorContext?: boolean; hasAdvisorContext?: boolean },
): FundOpportunityLookupResult<T> {
  if (isLoading) {
    return { investor: null, error: null, stillLoading: true };
  }

  if (options?.requireAdvisorContext && !options.hasAdvisorContext) {
    return { investor: null, error: 'No advisor context available.', stillLoading: false };
  }

  if (investors && investors.length > 0) {
    const investor =
      investors.find((entry) => entry.fundOpportunityId === fundOpportunityId) ?? null;

    if (investor) {
      return { investor, error: null, stillLoading: false };
    }

    return {
      investor: null,
      error: 'Fund opportunity not found or not accessible.',
      stillLoading: false,
    };
  }

  if (investors && investors.length === 0) {
    return { investor: null, error: 'No fund opportunities available.', stillLoading: false };
  }

  return { investor: null, error: null, stillLoading: true };
}

/**
 * Targeted fallback for a fund opportunity that resolveInvestorForFundOpportunity
 * couldn't find in the cached (COMPLETE-excluding) investors list — e.g. a closed
 * fund. Mirrors the native app: query by fundOpportunityId directly, which the
 * backend serves regardless of fund status.
 */
export async function findInvestorForFundOpportunityFallback<T>(
  backendService: { getInvestorsByFundOpportunityId: (fundOpportunityId: string) => Promise<T[]> },
  fundOpportunityId: string,
): Promise<T | null> {
  const investors = await backendService.getInvestorsByFundOpportunityId(fundOpportunityId);
  return investors[0] ?? null;
}
